import type { SchemaOrgNode } from './schema-org-node.model';

/**
 * Builds the universal Organization `@id`. Mirrors the EPS-3871 helper —
 * inlined here so EPS-3872 stays self-contained on its sibling branch.
 * When the stacks merge back into foundation, the duplication can be
 * collapsed into a single shared helper.
 */
function getOrganizationId(siteUrl: string): string {
  const root = siteUrl.endsWith('/') ? siteUrl : `${siteUrl}/`;
  return `${root}#organization`;
}

/**
 * Shape of a single article that can become a BlogPosting node. Components
 * normalize their CMS data to this shape before calling buildArticleListSchema.
 */
export interface ArticleItem {
  /** Headline / title — required. HTML stripped at build time. */
  headline: string;
  /** Absolute URL of the article. Used as the @id source. */
  url: string;
  /** Optional summary/description — will be HTML stripped. */
  description?: string;
  /** Optional image URL. */
  image?: string;
  /** ISO 8601 datetime or any parseable date string. */
  datePublished?: string;
  /** Last-modified ISO 8601 datetime, optional. */
  dateModified?: string;
  /** Category / section name (e.g. "Driving", "Real Estate"). */
  articleSection?: string;
  /** Schema.org type override — defaults to "BlogPosting". */
  articleType?: 'BlogPosting' | 'Article' | 'NewsArticle';
}

export interface ArticleListSchemaData {
  /** Stable identifier for the ItemList — typically `${pageUrl}#articles-${variant}`. */
  listId: string;
  /** Human-readable name for the ItemList. */
  listName: string;
  /** Site root for the Organization cross-reference (used as `author`). */
  siteUrl: string;
  /** Brand name used on the cross-referenced Organization. */
  organizationName: string;
  /** The article items to emit. */
  items: ArticleItem[];
}

/**
 * Builds an ItemList of Article references plus the Article nodes themselves.
 * Each Article cross-references the Organization via `author`. Returns null
 * when items is empty so the caller can register safely.
 *
 * Article `@id`s are derived from the article URL (`{url}#article`) so they
 * are stable across pages and dedup correctly when the same article appears
 * in multiple lists.
 */
export function buildArticleListSchema(data: ArticleListSchemaData | null): SchemaOrgNode[] | null {
  if (!data || !Array.isArray(data.items) || data.items.length === 0) return null;

  const orgRef = {
    '@id': getOrganizationId(data.siteUrl),
    '@type': 'EducationalOrganization' as const,
    name: data.organizationName,
  };

  const articleNodes: SchemaOrgNode[] = data.items
    .filter((item) => item && item.url && item.headline)
    .map((item) => buildArticleNode(item, orgRef));

  if (articleNodes.length === 0) return null;

  // SECURITY: never fall back to the raw listName — if stripHtml returns empty
  // (e.g. CMS title was `<a></a>`), we'd otherwise emit unsanitized HTML into
  // the JSON-LD `name` field. Drop the field instead.
  const cleanedName = stripHtml(data.listName);
  const itemList: SchemaOrgNode = {
    '@type': 'ItemList',
    '@id': data.listId,
    numberOfItems: articleNodes.length,
    itemListElement: articleNodes.map((article, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: { '@id': article['@id'] },
    })),
  };
  if (cleanedName) itemList['name'] = cleanedName;

  return [...articleNodes, itemList];
}

function buildArticleNode(
  item: ArticleItem,
  orgRef: { '@id': string; '@type': 'EducationalOrganization'; name: string },
): SchemaOrgNode {
  const headline = stripHtml(item.headline);
  const node: SchemaOrgNode = {
    '@type': item.articleType ?? 'BlogPosting',
    '@id': `${item.url}#article`,
    headline,
    url: item.url,
    author: orgRef,
  };
  const description = stripHtml(item.description ?? '');
  if (description) node['description'] = description;
  if (item.image) node['image'] = item.image;
  if (item.datePublished && isValidDate(item.datePublished)) {
    node['datePublished'] = isoDate(item.datePublished);
  }
  if (item.dateModified && isValidDate(item.dateModified)) {
    node['dateModified'] = isoDate(item.dateModified);
  }
  if (item.articleSection) {
    const section = stripHtml(item.articleSection);
    if (section) node['articleSection'] = section;
  }
  return node;
}

/**
 * Strip HTML tags from CMS-authored fields. Mirrors the pattern in
 * tabbed-content / review-schema utilities — drops anchors entirely, replaces
 * other tags with spaces, collapses whitespace.
 *
 * Defense-in-depth: input is bounded to 10KB to cap regex work on pathological
 * CMS payloads, HTML comments are stripped (the `<[^>]*>` rule does not match
 * across comment closers), and any leftover lone `<` / `>` are removed so they
 * cannot survive into JSON-LD even if a tag was malformed.
 */
const STRIP_HTML_MAX_LENGTH = 10_000;
function stripHtml(input: string | undefined): string {
  if (!input) return '';
  const bounded = input.length > STRIP_HTML_MAX_LENGTH ? input.slice(0, STRIP_HTML_MAX_LENGTH) : input;
  return bounded
    .replace(/<!--[\s\S]*?-->/g, '')
    .replace(/<a\b[^>]*>[\s\S]*?<\/a>/gi, '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/[<>]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/ ([.,!?;:])/g, '$1')
    .trim();
}

/**
 * Extracts a plain-text headline from a content blob. Looks for the title in:
 *   1. an `<h1>`-`<h6>` tag (e.g. `<h4>Title</h4><p>Summary</p>`)
 *   2. a `<strong>`-wrapped paragraph (e.g. `<p><strong>Title</strong></p><p>Summary</p>`)
 *      — the convention used by `parseReasourceContent` in entry-listings and
 *      the same shape that appears on career-center cards authored in CKEditor
 *   3. otherwise falls back to the first sentence of the stripped text
 */
export function extractHeadlineFromHtml(html: string | undefined): string {
  if (!html) return '';
  const headingMatch = html.match(/<h[1-6][^>]*>([\s\S]*?)<\/h[1-6]>/i);
  if (headingMatch) {
    return stripHtml(headingMatch[1]);
  }
  const strongMatch = html.match(/<strong[^>]*>([\s\S]*?)<\/strong>/i);
  if (strongMatch) {
    return stripHtml(strongMatch[1]);
  }
  return stripHtml(html).split('.')[0].trim();
}

/**
 * Extracts a plain-text description from a content blob — strips out the
 * heading (if present, in either `<h1>-<h6>` form or
 * `<p><strong>Title</strong></p>` form) and returns the remainder
 * HTML-stripped.
 */
export function extractDescriptionFromHtml(html: string | undefined): string {
  if (!html) return '';
  const withoutHeading = html
    .replace(/<h[1-6][^>]*>[\s\S]*?<\/h[1-6]>/gi, '')
    .replace(/<p[^>]*>\s*<strong[^>]*>[\s\S]*?<\/strong>\s*<\/p>/gi, '');
  return stripHtml(withoutHeading);
}

function isValidDate(input: string): boolean {
  return !Number.isNaN(Date.parse(input));
}

function isoDate(input: string): string {
  // Use full ISO with time when available — Schema.org accepts both YYYY-MM-DD
  // and full ISO 8601. Articles benefit from precise datePublished.
  return new Date(input).toISOString();
}
